import { useState, useMemo, useCallback } from 'react';
import type { CampaignData, CollectionKey } from '../../shared/types';

export interface SearchResult {
  id: number;
  category: CollectionKey;
  categoryLabel: string;
  title: string;
  subtitle?: string;
  matchedField: string;
  matchedText: string;
}

const categoryLabels: Record<CollectionKey, string> = {
  playerCharacters: 'Character',
  npcs: 'NPC',
  locations: 'Location',
  organizations: 'Organization',
  plotThreads: 'Plot',
  sessionNotes: 'Session',
  calendars: 'Calendar',
  calendarEvents: 'Event',
  futureContent: 'Future Content',
  wikiArticles: 'Wiki',
};

const searchableFields: Record<CollectionKey, string[]> = {
  playerCharacters: ['characterName', 'playerName', 'class', 'faction', 'notes', 'heatNotes'],
  npcs: ['name', 'description', 'location', 'notes'],
  locations: ['name', 'description', 'connections', 'notes'],
  organizations: ['name', 'type', 'goals', 'members', 'notes'],
  plotThreads: ['title', 'description', 'status', 'priority'],
  sessionNotes: ['summary', 'highlights', 'date'],
  calendars: ['name', 'description'],
  calendarEvents: ['title', 'description', 'date', 'time'],
  futureContent: ['title', 'type', 'description', 'prepNotes'],
  wikiArticles: ['title', 'type', 'summary', 'content'],
};

function getTitleForItem(item: Record<string, unknown>, category: CollectionKey): string {
  switch (category) {
    case 'playerCharacters':
      return String(item.characterName || '');
    case 'npcs':
    case 'locations':
    case 'organizations':
      return String(item.name || '');
    case 'plotThreads':
    case 'futureContent':
    case 'wikiArticles':
      return String(item.title || '');
    case 'sessionNotes':
      return `Session ${item.sessionNum}`;
    case 'calendars':
      return String(item.name || '');
    case 'calendarEvents':
      return String(item.title || '');
    default:
      return '';
  }
}

function getSubtitleForItem(item: Record<string, unknown>, category: CollectionKey): string | undefined {
  switch (category) {
    case 'playerCharacters':
      return `${item.class}${item.faction ? ` · ${item.faction}` : ''}`;
    case 'npcs':
      return item.location ? String(item.location) : undefined;
    case 'organizations':
      return item.type ? String(item.type) : undefined;
    case 'plotThreads':
      return `${item.status} · ${item.priority}`;
    case 'sessionNotes':
      return item.date ? String(item.date) : undefined;
    case 'calendars':
      return item.description ? String(item.description) : undefined;
    case 'calendarEvents':
      return item.date ? String(item.date) : undefined;
    case 'futureContent':
      return item.type ? String(item.type) : undefined;
    case 'wikiArticles':
      return item.type ? String(item.type) : undefined;
    default:
      return undefined;
  }
}

function highlightMatch(text: string, query: string): string {
  const index = text.toLowerCase().indexOf(query.toLowerCase());
  if (index === -1) return text;

  const start = Math.max(0, index - 30);
  const end = Math.min(text.length, index + query.length + 30);
  let result = text.slice(start, end);

  if (start > 0) result = '...' + result;
  if (end < text.length) result = result + '...';

  return result;
}

export function useGlobalSearch(campaignData: CampaignData) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const results = useMemo<SearchResult[]>(() => {
    if (!query.trim() || query.length < 2) return [];

    const searchResults: SearchResult[] = [];
    const lowerQuery = query.toLowerCase();

    const categories: CollectionKey[] = [
      'playerCharacters',
      'npcs',
      'locations',
      'organizations',
      'plotThreads',
      'sessionNotes',
      'calendars',
      'calendarEvents',
      'futureContent',
      'wikiArticles',
    ];

    for (const category of categories) {
      const items = campaignData[category] as Record<string, unknown>[];
      const fields = searchableFields[category];

      for (const item of items) {
        for (const field of fields) {
          const value = item[field];
          if (value && String(value).toLowerCase().includes(lowerQuery)) {
            searchResults.push({
              id: item.id as number,
              category,
              categoryLabel: categoryLabels[category],
              title: getTitleForItem(item, category),
              subtitle: getSubtitleForItem(item, category),
              matchedField: field,
              matchedText: highlightMatch(String(value), query),
            });
            break; // Only include each item once
          }
        }
      }
    }

    return searchResults.slice(0, 20); // Limit to 20 results
  }, [query, campaignData]);

  const openSearch = useCallback(() => setIsOpen(true), []);
  const closeSearch = useCallback(() => {
    setIsOpen(false);
    setQuery('');
  }, []);

  return {
    query,
    setQuery,
    results,
    isOpen,
    openSearch,
    closeSearch,
  };
}
